/**
 * Copy variants for the standalone "Why you should join PODGO" landing page.
 * The header lets a visitor switch the narrative; the two stories change the
 * hero copy plus the order/numbering of the two middle sections.
 */

export type JoinStory = "simplicity" | "money";

/** Story labels + one-line context, shared by the page picker and the mega menu. */
export const JOIN_STORIES: { key: JoinStory; label: string; hint: string }[] = [
  { key: "simplicity", label: "Stop juggling tools", hint: "One operator runs hosting, editing, clips and ads." },
  { key: "money", label: "Make more money", hint: "See every dollar your show earns, and get paid faster." },
];

export const isJoinStory = (v: string | null): v is JoinStory =>
  v === "simplicity" || v === "money";

/** Context line for a menu link pointing at a story, e.g. `?story=money`. */
export function joinStoryHint(href: string | null): string | null {
  const m = href?.match(/[?&]story=([a-z]+)/);
  return JOIN_STORIES.find((s) => s.key === m?.[1])?.hint ?? null;
}

export interface JoinStoryCopy {
  heroTitle: string;
  heroSub: string;
  moneyTitle: string;
  order: { stack: number; money: number };
  num: { stack: string; money: string };
}

export const JOIN_COPY: Record<JoinStory, JoinStoryCopy> = {
  simplicity: {
    heroTitle:
      "You started a show. Somehow you ended up running an operations department.",
    heroSub:
      "Hosting in one place. Editing in another. Clips somewhere else. An ad rep who emails twice a quarter. PODGO is one operator that runs the whole stack — and you keep your show, your feed, and your audience.",
    moneyTitle:
      "Every dollar your show makes, on one screen — and cash against it when you need it.",
    order: { stack: 2, money: 3 },
    num: { stack: "02", money: "03" },
  },
  money: {
    heroTitle: "Your show is already worth more than it's paying you.",
    heroSub:
      "Ads you can't price, on inventory you can't see, paid out on terms you didn't negotiate. PODGO sells your inventory, shows you every dollar it earns, and can advance you cash against it — then quietly runs the other eleven jobs too.",
    moneyTitle:
      "Every dollar your show makes, on one screen — and cash against it when you need it.",
    order: { stack: 3, money: 2 },
    num: { stack: "03", money: "02" },
  },
};

export const JOIN_CHIPS = [
  "Hosting",
  "Editing",
  "Clip cutting",
  "Transcription",
  "Translation",
  "Ad sales",
  "Analytics",
  "Merch & storefront",
  "Payouts",
  "Scheduling",
  "Rights admin",
  "Distribution",
];

/** Absolute placements mirroring the original ring layout (desktop only). */
export const JOIN_CHIP_POSITIONS = [
  { left: "1%", top: "8%" },
  { left: "22%", top: "0%" },
  { left: "47%", top: "5%" },
  { left: "71%", top: "1%" },
  { left: "86%", top: "24%" },
  { left: "83%", top: "56%" },
  { left: "63%", top: "80%" },
  { left: "41%", top: "88%" },
  { left: "17%", top: "76%" },
  { left: "0%", top: "52%" },
  { left: "11%", top: "29%" },
  { left: "76%", top: "40%" },
];

export const JOIN_ANSWERS = [
  {
    q: "“Do I lose control of my show?”",
    a: "No. You own the show, the name, the IP, the feed and the audience relationship. PODGO operates the machinery around it. Leave and all of it walks out with you.",
  },
  {
    q: "“Am I locked into something?”",
    a: "No long-term contract. Plans run month to month — move up a tier, move down, or leave, and your show goes with you. If PODGO isn't out-earning what you had, walking away should be easy.",
  },
  {
    q: "“I'm too small for this.”",
    a: "A three-person network and a one-person show run on the same rails. Production, distribution and analytics are on from day one, and ad inventory switches on as soon as your downloads support it.",
  },
  {
    q: "“Migrating will break my feed.”",
    a: "Your listeners never notice. We 301-redirect from your current host, preserve every episode GUID, and watch the numbers for 30 days. No re-subscribes, no lost back catalogue.",
  },
  {
    q: "“I already have an ad rep.”",
    a: "Keep them. PODGO can run underneath an existing rep — programmatic fill, subscriptions, merch and production only — and take nothing on the deals they close.",
  },
  {
    q: "“So what does it cost me?”",
    a: "Choose the paid plan that fits your podcast. See our pricing page for current plans and what's included.",
    highlighted: true,
  },
];
